// Responsive image helper.
//
// Optimized WebP variants live in `src/assets/optimized/<name>-<width>.webp`.
// They are bundled by Vite (hashed, served from the site origin) so they work
// on Lovable, Vercel, or any host without CDN rewriting.
//
// `pic("solar-detail")` returns the `src` (largest variant) and a `srcSet`
// string spanning every generated width, so the browser can pick the smallest
// file that fits the displayed size.

const files = import.meta.glob("/src/assets/optimized/*.webp", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

export type ResponsivePicture = {
  /** Largest variant — safe default `src`. */
  src: string;
  /** Comma-separated "url <width>w" descriptors for the `srcSet` attribute. */
  srcSet: string;
};

type Variant = { width: number; url: string };

const groups: Record<string, Variant[]> = {};

for (const [path, url] of Object.entries(files)) {
  const file = path.split("/").pop() ?? "";
  const match = file.match(/^(.+)-(\d+)\.webp$/);
  if (!match) continue;
  const [, name, width] = match;
  (groups[name] ??= []).push({ width: Number(width), url });
}

for (const name of Object.keys(groups)) {
  groups[name].sort((a, b) => a.width - b.width);
}

/** Look up the responsive variants generated for a given base image name. */
export function pic(name: string): ResponsivePicture {
  const variants = groups[name];
  if (!variants || variants.length === 0) {
    // Fail loudly in dev; return empty strings so the build still completes.
    if (import.meta.env.DEV) {
      console.warn(`[responsive-images] no variants found for "${name}"`);
    }
    return { src: "", srcSet: "" };
  }
  const largest = variants[variants.length - 1];
  return {
    src: largest.url,
    srcSet: variants.map((v) => `${v.url} ${v.width}w`).join(", "),
  };
}
